import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Send, Trash2, Loader2, Bot, User } from 'lucide-react';
import { Button } from './ui/button';
import API_URL from '../config/api';

const AIChatbot = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Welcome messages in all languages
  const welcomeMessages = {
    fr: "Bonjour ! 👋 Je suis l'assistant virtuel du Marathon de l'Avenir EDENLIFE 2026. Je peux vous aider avec:\n\n• Les 5 prix à gagner (jusqu'à $450,000!) 🏆\n• Comment acheter des billets 🎟️\n• Les méthodes de paiement (Carte, PayPal, Interac, Moncash, Natcash, Crypto) 💳\n• Le programme d'affiliation (10% commission) 💰\n• Informations sur EDENLIFE International 🌍\n\n🎫 **Prêt à tenter votre chance ?**\n👉 [Inscrivez-vous maintenant](/auth)\n👉 [Achetez vos billets](/purchase)\n\nComment puis-je vous aider aujourd'hui ?",
    en: "Hello! 👋 I'm the Marathon de l'Avenir EDENLIFE 2026 virtual assistant. I can help you with:\n\n• The 5 prizes to win (up to $450,000!) 🏆\n• How to buy tickets 🎟️\n• Payment methods (Card, PayPal, Interac, Moncash, Natcash, Crypto) 💳\n• Affiliate program (10% commission) 💰\n• EDENLIFE International information 🌍\n\n🎫 **Ready to try your luck?**\n👉 [Sign up now](/auth)\n👉 [Buy your tickets](/purchase)\n\nHow can I help you today?",
    ht: "Bonjou! 👋 Mwen se asistan vityèl Marathon de l'Avenir EDENLIFE 2026. Mwen ka ede ou ak:\n\n• 5 pri pou genyen (jiska $450,000!) 🏆\n• Kijan pou achte tikè 🎟️\n• Metòd peman (Kat, PayPal, Interac, Moncash, Natcash, Crypto) 💳\n• Pwogram afilyasyon (10% komisyon) 💰\n• Enfòmasyon sou EDENLIFE International 🌍\n\n🎫 **Pare pou eseye chans ou?**\n👉 [Enskri kounye a](/auth)\n👉 [Achte tikè ou yo](/purchase)\n\nKijan mwen ka ede ou jodi a?",
    es: "¡Hola! 👋 Soy el asistente virtual del Marathon de l'Avenir EDENLIFE 2026. Puedo ayudarte con:\n\n• Los 5 premios a ganar (¡hasta $450,000!) 🏆\n• Cómo comprar boletos 🎟️\n• Métodos de pago (Tarjeta, PayPal, Interac, Moncash, Natcash, Crypto) 💳\n• Programa de afiliados (10% comisión) 💰\n• Información sobre EDENLIFE International 🌍\n\n🎫 **¿Listo para probar suerte?**\n👉 [Regístrate ahora](/auth)\n👉 [Compra tus boletos](/purchase)\n\n¿Cómo puedo ayudarte hoy?",
    pt: "Olá! 👋 Sou o assistente virtual do Marathon de l'Avenir EDENLIFE 2026. Posso ajudá-lo com:\n\n• Os 5 prêmios a ganhar (até $450,000!) 🏆\n• Como comprar bilhetes 🎟️\n• Métodos de pagamento (Cartão, PayPal, Interac, Moncash, Natcash, Crypto) 💳\n• Programa de afiliados (10% comissão) 💰\n• Informações sobre EDENLIFE International 🌍\n\n🎫 **Pronto para tentar a sorte?**\n👉 [Cadastre-se agora](/auth)\n👉 [Compre seus bilhetes](/purchase)\n\nComo posso ajudá-lo hoje?"
  };

  const getWelcomeMessage = () => ({
    role: 'assistant',
    content: welcomeMessages[i18n.language] || welcomeMessages.fr
  });

  useEffect(() => {
    // Load session from localStorage
    const savedSession = localStorage.getItem('chatbot_session');
    if (savedSession) {
      const parsed = JSON.parse(savedSession);
      setSessionId(parsed.sessionId);
      setMessages(parsed.messages || [getWelcomeMessage()]);
    } else {
      setMessages([getWelcomeMessage()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Save session to localStorage
    if (sessionId) {
      localStorage.setItem('chatbot_session', JSON.stringify({
        sessionId,
        messages
      }));
    }
  }, [sessionId, messages]);

  useEffect(() => {
    // Scroll to bottom when messages change
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Focus input when chat opens
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Textes de l'interface traduits
  const uiTexts = {
    fr: {
      title: 'Assistant EDENLIFE',
      subtitle: 'En ligne • Réponse instantanée',
      placeholder: 'Tapez votre message...',
      clearChat: 'Nouvelle conversation',
      error: "Désolé, je rencontre des difficultés techniques. Veuillez réessayer ou contacter notre équipe via WhatsApp.",
      typing: "En train d'écrire...",
      poweredBy: "Propulsé par GPT • EDENLIFE International"
    },
    en: {
      title: 'EDENLIFE Assistant',
      subtitle: 'Online • Instant response',
      placeholder: 'Type your message...',
      clearChat: 'New conversation',
      error: "Sorry, I'm experiencing technical difficulties. Please try again or contact our team via WhatsApp.",
      typing: "Typing...",
      poweredBy: "Powered by GPT • EDENLIFE International"
    },
    ht: {
      title: 'Asistan EDENLIFE',
      subtitle: 'Anliy • Repons imedya',
      placeholder: 'Tape mesaj ou...',
      clearChat: 'Nouvo konvèsasyon',
      error: "Eskize, mwen gen difikilte teknik. Tanpri eseye ankò oswa kontakte ekip nou sou WhatsApp.",
      typing: "Ap ekri...",
      poweredBy: "Sipòte pa GPT • EDENLIFE International"
    },
    es: {
      title: 'Asistente EDENLIFE',
      subtitle: 'En línea • Respuesta instantánea',
      placeholder: 'Escribe tu mensaje...',
      clearChat: 'Nueva conversación',
      error: "Lo siento, estoy teniendo dificultades técnicas. Por favor intenta de nuevo o contacta a nuestro equipo vía WhatsApp.",
      typing: "Escribiendo...",
      poweredBy: "Impulsado por GPT • EDENLIFE International"
    },
    pt: {
      title: 'Assistente EDENLIFE',
      subtitle: 'Online • Resposta instantânea',
      placeholder: 'Digite sua mensagem...',
      clearChat: 'Nova conversa',
      error: "Desculpe, estou com dificuldades técnicas. Por favor tente novamente ou contate nossa equipe via WhatsApp.",
      typing: "Digitando...",
      poweredBy: "Desenvolvido por GPT • EDENLIFE International"
    }
  };

  const ui = uiTexts[i18n.language] || uiTexts.fr;

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = { role: 'user', content: inputValue.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.content,
          session_id: sessionId,
          language: i18n.language || 'fr'
        }),
      });

      const data = await response.json();
      
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
      }

      const assistantMessage = { 
        role: 'assistant', 
        content: data.response 
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: ui.error 
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearChat = async () => {
    if (sessionId) {
      try {
        await fetch(`${API_URL}/chat/${sessionId}`, {
          method: 'DELETE',
        });
      } catch (error) {
        console.error('Error clearing chat:', error);
      }
    }
    setMessages([getWelcomeMessage()]);
    setSessionId(null);
    localStorage.removeItem('chatbot_session');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Convert markdown links to clickable elements
  const formatMessage = (content) => {
    // Regular expression to match markdown links [text](url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    
    return content.split('\n').map((line, lineIdx) => {
      const parts = [];
      let lastIndex = 0;
      let match;
      
      // Reset regex lastIndex for each line
      linkRegex.lastIndex = 0;
      
      // Find all links in the line
      while ((match = linkRegex.exec(line)) !== null) {
        // Add text before the link
        if (match.index > lastIndex) {
          parts.push(line.substring(lastIndex, match.index));
        }
        
        // Add the link as a clickable element
        const linkText = match[1];
        const linkUrl = match[2];
        
        parts.push(
          <button
            key={`link-${lineIdx}-${match.index}`}
            onClick={() => {
              setIsOpen(false); // Close chatbot
              navigate(linkUrl); // Use React Router navigation
            }}
            className="text-primary font-semibold underline hover:text-primary/80 cursor-pointer bg-transparent border-none p-0 inline"
          >
            {linkText}
          </button>
        );
        
        lastIndex = match.index + match[0].length;
      }
      
      // Add remaining text after last link
      if (lastIndex < line.length) {
        parts.push(line.substring(lastIndex));
      }
      
      // If no links found, just return the line
      if (parts.length === 0) {
        parts.push(line);
      }
      
      return (
        <span key={lineIdx}>
          {parts}
          {lineIdx < content.split('\n').length - 1 && <br />}
        </span>
      );
    });
  };

  return (
    <>
      {/* Chat Button - Repositioned for mobile */}
      <button
        onClick={() => setIsOpen(true)}
        data-testid="chatbot-toggle-button"
        className={`fixed bottom-6 right-4 sm:bottom-24 sm:right-6 z-40 bg-gradient-to-r from-primary to-secondary text-white p-3 sm:p-4 rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 ${isOpen ? 'hidden' : 'flex'} items-center justify-center`}
        aria-label="Ouvrir le chat"
      >
        <Bot className="w-5 h-5 sm:w-6 sm:h-6" />
        <span className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-accent rounded-full animate-pulse"></span>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          data-testid="chatbot-window"
          className="fixed bottom-0 right-0 sm:bottom-4 sm:right-4 z-50 w-full sm:w-[360px] md:w-[400px] h-[85vh] sm:h-[550px] bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden border-t sm:border border-gray-200"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-secondary p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">{ui.title}</h3>
                <p className="text-white/80 text-xs">{ui.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleClearChat}
                data-testid="chatbot-clear-button"
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                title={ui.clearChat}
              >
                <Trash2 className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                data-testid="chatbot-close-button"
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Messages Container */}
          <div 
            data-testid="chatbot-messages"
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-primary text-white rounded-br-md'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                  }`}
                >
                  {message.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                      <Bot className="w-4 h-4 text-primary" />
                      <span className="text-xs font-medium text-primary">{ui.title}</span>
                    </div>
                  )}
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {formatMessage(message.content)}
                  </p>
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm text-gray-500">{ui.typing}</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={ui.placeholder}
                data-testid="chatbot-input"
                className="flex-1 px-4 py-3 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                disabled={isLoading}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                data-testid="chatbot-send-button"
                className="w-11 h-11 rounded-full p-0 bg-primary hover:bg-primary/90 disabled:opacity-50"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-xs text-gray-400 text-center mt-2">
              {ui.poweredBy}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatbot;
